import React from 'react';
import { useTranslation } from 'utils/i18n';
import { StaticPageShell } from 'feSharedIndex';

// Import extracted logic
import translations from './translations';
import { useChangelogUI } from './logic/ui';

/**
 * Changelog Component
 * Displays changelog information. Uses shared StaticPageShell; theme from config.
 */
const Changelog = () => {
  const { language } = useTranslation();
  useChangelogUI();

  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  return (
    <StaticPageShell
      title={t('title', 'Changelog')}
      lead={t('subtitle', 'All updates, new features and improvements to the B-esper platform.')}
      contentClassName="static-page-content"
    >
        <section className="content-section changelog-version">
          <div className="changelog-version-header">
            <h2>Version 2.4.0</h2>
            <div className="changelog-version-meta">
              <span className="release-date">14. September 2024</span>
              <span className="release-type new">Major Release</span>
            </div>
          </div>
          
          <div className="changelog-categories">
            <div className="changelog-category">
              <h3 className="changelog-category-title new">
                <span className="changelog-icon">✨</span>
                {t('categories.new', 'New Features')}
              </h3>
              <ul className="changelog-list">
                <li>Multi-Tenant Architektur für Enterprise-Kunden</li>
                <li>Erweiterte Analytics mit Echtzeit-Dashboards</li>
                <li>Verbessertes Ressourcen-Management Interface</li>
                <li>Mobile-optimierte Benutzeroberfläche</li>
              </ul>
            </div>
            
            <div className="changelog-category">
              <h3 className="changelog-category-title improvement">
                <span className="changelog-icon">⚡</span>
                {t('categories.improvements', 'Improvements')}
              </h3>
              <ul className="changelog-list">
                <li>50% schnellere API-Antwortzeiten</li>
                <li>Überarbeitetes User Management mit granularen Berechtigungen</li>
                <li>Verbesserte Chatbot-Konfiguration mit Live-Preview</li>
              </ul>
            </div>
            
            <div className="changelog-category">
              <h3 className="changelog-category-title fix">
                <span className="changelog-icon">🐛</span>
                {t('categories.fixes', 'Bug Fixes')}
              </h3>
              <ul className="changelog-list">
                <li>Behoben: Authentifizierungsfehler bei Entra External ID</li>
                <li>Behoben: Inkorrekte Darstellung in mobilen Browsern</li>
                <li>Behoben: Timeout-Probleme bei großen Datenexporten</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section changelog-version">
          <div className="changelog-version-header">
            <h2>Version 2.3.2</h2>
            <div className="changelog-version-meta">
              <span className="release-date">28. August 2024</span>
              <span className="release-type fix">Patch Release</span>
            </div>
          </div>
          
          <div className="changelog-categories">
            <div className="changelog-category">
              <h3 className="changelog-category-title fix">
                <span className="changelog-icon">🐛</span>
                {t('categories.fixes', 'Bug Fixes')}
              </h3>
              <ul className="changelog-list">
                <li>Stabilisierte Verbindung zu externen AI-Services</li>
                <li>Behoben: Memory Leak in der Chatbot-Engine</li>
                <li>Verbesserte Fehlerbehandlung bei API-Aufrufen</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section changelog-version">
          <div className="changelog-version-header">
            <h2>Version 2.3.0</h2>
            <div className="changelog-version-meta">
              <span className="release-date">15. August 2024</span>
              <span className="release-type new">Minor Release</span>
            </div>
          </div>
          
          <div className="changelog-categories">
            <div className="changelog-category">
              <h3 className="changelog-category-title new">
                <span className="changelog-icon">✨</span>
                {t('categories.new', 'New Features')}
              </h3>
              <ul className="changelog-list">
                <li>Stripe-Integration für automatisierte Abrechnung</li>
                <li>Erweiterte DSGVO-Compliance-Tools</li>
                <li>Neue API-Endpunkte für Partner-Integrationen</li>
                <li>Backup und Restore-Funktionalität</li>
              </ul>
            </div>
            
            <div className="changelog-category">
              <h3 className="changelog-category-title improvement">
                <span className="changelog-icon">⚡</span>
                {t('categories.improvements', 'Improvements')}
              </h3>
              <ul className="changelog-list">
                <li>Optimierte Datenbankperformance</li>
                <li>Überarbeitete Benutzeroberfläche mit modernem Design</li>
                <li>Erweiterte Logging und Monitoring-Funktionen</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section changelog-version">
          <div className="changelog-version-header">
            <h2>Version 2.2.1</h2>
            <div className="changelog-version-meta">
              <span className="release-date">2. August 2024</span>
              <span className="release-type improvement">Patch Release</span>
            </div>
          </div>
          
          <div className="changelog-categories">
            <div className="changelog-category">
              <h3 className="changelog-category-title improvement">
                <span className="changelog-icon">⚡</span>
                {t('categories.improvements', 'Improvements')}
              </h3>
              <ul className="changelog-list">
                <li>Erweiterte Sicherheitsfeatures für Enterprise-Umgebungen</li>
                <li>Bessere Performance bei gleichzeitigen Benutzerzugriffen</li>
                <li>Optimierte Caching-Strategien</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{t('releaseCycles.heading', 'Release Cycles')}</h2>
          <p>
            {t('releaseCycles.description', 'We publish regular updates to the B-esper platform:')}
          </p>
          <div className="release-cycle-grid">
            <div className="release-cycle-item">
              <h3>{t('releaseCycles.major.title', 'Major Releases')}</h3>
              <p>{t('releaseCycles.major.description', 'Quarterly with new features and major improvements')}</p>
            </div>
            <div className="release-cycle-item">
              <h3>{t('releaseCycles.minor.title', 'Minor Releases')}</h3>
              <p>{t('releaseCycles.minor.description', 'Monthly with improvements and new features')}</p>
            </div>
            <div className="release-cycle-item">
              <h3>{t('releaseCycles.patch.title', 'Patch Releases')}</h3>
              <p>{t('releaseCycles.patch.description', 'As needed for critical bug fixes and security updates')}</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{t('feedback.heading', 'Feedback on Updates')}</h2>
          <p>
            {t('feedback.description', 'Do you have feedback on our updates or suggestions for new features? Please contact us:')}
          </p>
          <div className="contact-details">
            <p><strong>{t('feedback.email', 'Email:')}</strong> <a href="mailto:feedback@b-esper.com" className="inline-link">feedback@b-esper.com</a></p>
            <p><strong>{t('feedback.support', 'Support:')}</strong> <a href="mailto:support@b-esper.com" className="inline-link">support@b-esper.com</a></p>
          </div>
        </section>
    </StaticPageShell>
  );
};

export default Changelog;


